/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Page History Orders component
 *
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Misha Kokoulin
 * @author Anton Panasenko
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */
import { Fragment, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { renderDropFilterButton } from '@components/common/PageTitle/PageTitle';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectPageOrderTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging';
import {
    tableIsErrorSelector,
    tableIsLoadedWithDataSelector,
    tableIsLoadingSelector,
    tableMetaFilterSelector,
} from '@modules/table/selectors';

import { PageTitle } from '../../components/common';
import Title from '../../components/common/Title/Title';
import PageLocale from '../../modules/internationalization/components/PageLocale';
import {
    initialise,
    Table,
    TableDensity,
    TableExport,
    useTableFilterApi,
    useTableListApi,
    useTableMetaApi,
    useTableResizeApi,
} from '../../modules/table';
import TableOrderColumns from './components/TableOrderColumns';
import { useSendOrdersListViewedMutation } from './store';
import { ITableOrder } from './types';
import { tableOrderListenerMiddleware, tableOrderListenerStart } from './utils/tableMetaListenerMiddleware';

const TABLE_COLUMNS: Array<keyof ITableOrder> = ['action', 'date', 'status', 'subsCount', 'user'];

export const PageHistoryOrder = () => {
    const dispatch = useDispatch();

    const pageTranslation = useSelector(makeSelectPageOrderTranslation());
    const commonTranslation = useSelector(makeSelectTranslationCommon());

    /* Table Statuses  */
    const isLoading = useSelector(tableIsLoadingSelector);
    const isError = useSelector(tableIsErrorSelector);
    const isLoadedWithData = useSelector(tableIsLoadedWithDataSelector);

    const [sendOrdersListViewed] = useSendOrdersListViewedMutation();

    const { instructionsLinks: { ordersGeneral, ordersExport } } = commonTranslation;

    /* Table Meta  */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const config = useMemo(() => TableOrderColumns(pageTranslation, commonTranslation), [pageTranslation]);
    const { columns } = useTableMetaApi(config);

    /* Table List */
    const { data, count, isCompact, infiniteLoader } = useTableListApi();

    /* Table Filter */
    const { drop } = useTableFilterApi();

    const tableFilterSelector = useMemo(() => tableMetaFilterSelector('orders/meta', TABLE_COLUMNS), []);
    const selectedFilters = useSelector(tableFilterSelector);

    /* Table Resize */
    const { resize } = useTableResizeApi();

    /* Инициализация таблицы */
    useEffect(() => {
        dispatch(
            initialise({
                meta: {
                    url: 'orders/meta',
                },
                list: {
                    url: 'orders/list',
                    log: {
                        eventName: ELogBusinessEventName.B2B_ORDER_HISTORY,
                        type: ELogBusinessEventType.common,
                    },
                },
                filterAndSort: {
                    url: 'orders/filter-and-sort',
                    suggest: 'orders/suggest',
                },
                resize: {
                    url: 'orders/fields/resize',
                },
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Middleware для обработки api запросов таблицы */
    useEffect(() => {
        tableOrderListenerStart();
        return () => {
            tableOrderListenerMiddleware.clearListeners();
        };
    }, []);

    useEffect(() => {
        if (isLoadedWithData) sendOrdersListViewed();
    }, [isLoadedWithData, sendOrdersListViewed]);

    return (
        <Fragment>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageHistoryOrder]} />
            <Title pageName={ECommonTranslationPages.historyOrder} />

            <PageTitle
                count={count}
                dataName="historyOrder"
                instructionLink={ordersGeneral}
                title={pageTranslation.title.label}
            >
                {
                    // render drop filter button
                    !!Object.keys(selectedFilters).length &&
                        renderDropFilterButton({
                            onClick: drop,
                            value: pageTranslation.title.dropFilters,
                        })
                }
                <TableExport
                    instructionLink={ordersExport}
                    url="orders/export"
                />
                <TableDensity />
            </PageTitle>

            <Table
                columns={columns}
                data={data}
                dataName="historyOrders"
                dataNameRow="action.id"
                infiniteScroll={infiniteLoader}
                isCompact={isCompact}
                isError={isError}
                isLoading={isLoading}
                onResize={resize}
            />
        </Fragment>
    );
};

export default PageHistoryOrder;
